import React, { useState } from "react";
import ReactPlayer from "react-player";
import styled from "styled-components";

interface LazyVideoPlayerProps {
  url: string;
  options?: {
    controls?: boolean;
    rel?: number;
    color?: string;
    disablekb?: number;
    modestbranding?: number;
    [key: string]: string | number | boolean | undefined;
  };
  thumbnailUrl?: string;
  posterUrl?: string;
}

const VideoWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 850px;
  max-height: 480px;
  aspect-ratio: 16 / 9;
  @supports not (aspect-ratio: 16 / 9) {
    position: relative;
    padding-top: 56.25%;
  }
`;

const VideoThumbnail = styled.div`
  position: absolute;
  inset: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
  border-radius: 8px;
  overflow: hidden;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.02);
  }
`;

const ThumbnailImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PlayButton = styled.div`
  position: relative;
  z-index: 1;
  width: 80px;
  height: 80px;
  background: rgba(0, 0, 0, 0.8);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  border: 3px solid white;

  &:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: scale(1.1);
  }
`;

const PlayIcon = styled.div`
  width: 0;
  height: 0;
  border-left: 24px solid white;
  border-top: 16px solid transparent;
  border-bottom: 16px solid transparent;
  margin-left: 4px;
`;

const PlayerWrapper = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border-radius: 8px;
  overflow: hidden;
`;

export const LazyVideoPlayer: React.FC<LazyVideoPlayerProps> = ({
  url,
  options,
  thumbnailUrl: customThumbnailUrl,
  posterUrl: customPosterUrl,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);

  const handlePlay = () => {
    setIsLoaded(true);
  };

  const getThumbnailUrl = (): string => {
    if (customThumbnailUrl) return customThumbnailUrl;

    if (url.includes("youtube.com") || url.includes("youtu.be")) {
      const videoIdMatch = url.match(
        /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
      );
      if (videoIdMatch?.[1]) {
        return `https://img.youtube.com/vi/${videoIdMatch[1]}/hqdefault.jpg`;
      }
    }

    return customPosterUrl || "";
  };

  const thumbnailUrl = getThumbnailUrl();

  if (!isLoaded) {
    return (
      <VideoWrapper>
        <VideoThumbnail onClick={handlePlay}>
          <ThumbnailImage src={thumbnailUrl} alt="Video thumbnail" />
          <PlayButton>
            <PlayIcon />
          </PlayButton>
        </VideoThumbnail>
      </VideoWrapper>
    );
  }

  return (
    <VideoWrapper>
      <PlayerWrapper>
        <ReactPlayer
          url={url}
          width="100%"
          height="100%"
          controls
          playing={true}
          config={{
            youtube: {
              playerVars: {
                rel: 0,
                modestbranding: 1,
                playsinline: 1,
                fs: 1,
                autoplay: 1,
                ...options,
              },
            },
            vimeo: {
              playerOptions: {
                autoplay: true,
                ...options,
              },
            },
            file: {
              attributes: {
                poster: customPosterUrl,
                ...options,
              },
            },
          }}
        />
      </PlayerWrapper>
    </VideoWrapper>
  );
};

export default LazyVideoPlayer;
